import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SEO from '../SEO/SEO';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import api from '../../services/api';

import { Award, BookOpen, CheckCircle2, Heart, Sparkles, Target, TrendingUp, Users } from 'lucide-react';
import LifeAtAhamModal from '../Modals/LifeAtAhamModal';
import './LifeAtAham.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const baseUrl = API_URL ? (API_URL.endsWith('/api') ? API_URL.replace('/api', '') : API_URL) : '';

const LifeAtAham = () => {
  const navigate = useNavigate();
  const [banner, setBanner] = useState(null);
  const [cultureSettings, setCultureSettings] = useState(null);
  const [cultureValues, setCultureValues] = useState([]);
  const [cultureImages, setCultureImages] = useState([]);
  const [thriveSettings, setThriveSettings] = useState(null);
  const [thriveFeatures, setThriveFeatures] = useState([]);
  const [thriveImages, setThriveImages] = useState([]);
  const [journeySettings, setJourneySettings] = useState(null);
  const [journeyOpportunities, setJourneyOpportunities] = useState([]);
  const [journeyImages, setJourneyImages] = useState([]);
  const [beyondSettings, setBeyondSettings] = useState(null);
  const [beyondHighlights, setBeyondHighlights] = useState([]);
  const [beyondImages, setBeyondImages] = useState([]);
  const [recognitionSettings, setRecognitionSettings] = useState(null);
  const [recognitionAwards, setRecognitionAwards] = useState([]);
  const [recognitionImages, setRecognitionImages] = useState([]);
  const [ctaSettings, setCtaSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [bannerRes, settingsRes, valuesRes, imagesRes, thriveSettingsRes, thriveFeaturesRes, thriveImagesRes, journeySettingsRes, journeyOpportunitiesRes, journeyImagesRes, beyondSettingsRes, beyondHighlightsRes, beyondImagesRes, recognitionSettingsRes, recognitionAwardsRes, recognitionImagesRes, ctaSettingsRes] = await Promise.all([
          api.get('/admin/banners/active?page_type=life_at_aham'),
          api.get('/admin/life-at-aham/culture/settings'),
          api.get('/admin/life-at-aham/culture/values'),
          api.get('/admin/life-at-aham/culture/images'),
          api.get('/admin/life-at-aham/thrive/settings'),
          api.get('/admin/life-at-aham/thrive/features'),
          api.get('/admin/life-at-aham/thrive/images'),
          api.get('/admin/life-at-aham/journey/settings'),
          api.get('/admin/life-at-aham/journey/opportunities'),
          api.get('/admin/life-at-aham/journey/images'),
          api.get('/admin/life-at-aham/beyond/settings'),
          api.get('/admin/life-at-aham/beyond/highlights'),
          api.get('/admin/life-at-aham/beyond/images'),
          api.get('/admin/life-at-aham/recognition/settings'),
          api.get('/admin/life-at-aham/recognition/awards'),
          api.get('/admin/life-at-aham/recognition/images'),
          api.get('/admin/life-at-aham/cta/settings')
        ]);

        const activeBanner = (bannerRes.data.data || []).find(
          b => b.page_type === 'life_at_aham' && b.is_active
        );
        setBanner(activeBanner);

        if (settingsRes.data.success) setCultureSettings(settingsRes.data.data);
        if (valuesRes.data.success) setCultureValues(valuesRes.data.data.filter(v => v.is_active));
        if (imagesRes.data.success) setCultureImages(imagesRes.data.data.filter(i => i.is_active));

        // Thrive Data
        if (thriveSettingsRes.data.success) setThriveSettings(thriveSettingsRes.data.data);
        if (thriveFeaturesRes.data.success) setThriveFeatures(thriveFeaturesRes.data.data.filter(f => f.is_active));
        if (thriveImagesRes.data.success) setThriveImages(thriveImagesRes.data.data.filter(i => i.is_active));

        // Journey Data
        if (journeySettingsRes.data.success) setJourneySettings(journeySettingsRes.data.data);
        if (journeyOpportunitiesRes.data.success) setJourneyOpportunities(journeyOpportunitiesRes.data.data.filter(o => o.is_active));
        if (journeyImagesRes.data.success) setJourneyImages(journeyImagesRes.data.data.filter(i => i.is_active));

        // Beyond Data
        if (beyondSettingsRes.data.success) setBeyondSettings(beyondSettingsRes.data.data);
        if (beyondHighlightsRes.data.success) setBeyondHighlights(beyondHighlightsRes.data.data.filter(h => h.is_active));
        if (beyondImagesRes.data.success) setBeyondImages(beyondImagesRes.data.data.filter(i => i.is_active));

        // Recognition Data
        if (recognitionSettingsRes.data.success) setRecognitionSettings(recognitionSettingsRes.data.data);
        if (recognitionAwardsRes.data.success) setRecognitionAwards(recognitionAwardsRes.data.data.filter(a => a.is_active));
        if (recognitionImagesRes.data.success) setRecognitionImages(recognitionImagesRes.data.data.filter(i => i.is_active));

        // CTA Data
        if (ctaSettingsRes.data.success) setCtaSettings(ctaSettingsRes.data.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      Heart: <Heart className="w-6 h-6" />,
      Sparkles: <Sparkles className="w-6 h-6" />,
      Target: <Target className="w-6 h-6" />,
      BookOpen: <BookOpen className="w-6 h-6" />,
      Award: <Award className="w-5 h-5" />,
      Users: <Users className="w-5 h-5" />,
      TrendingUp: <TrendingUp className="w-5 h-5" />
    };
    return icons[iconName] || <Sparkles className="w-6 h-6" />;
  };

  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${path}`;
  };

  const values = [
    { icon: <Heart className="w-6 h-6" />, title: 'Respect for every individual' },
    { icon: <Sparkles className="w-6 h-6" />, title: 'Openness in thoughts and solutions' },
    { icon: <Target className="w-6 h-6" />, title: 'Ownership in every action' },
    { icon: <BookOpen className="w-6 h-6" />, title: 'Continuous learning as a habit' }
  ];

  const environmentFeatures = [
    'Flat communication, no unnecessary hierarchy',
    'Freedom to explore and try new ideas',
    'Supportive team culture',
    'Transparent and constructive feedback systems'
  ];

  const growthOpportunities = [
    { icon: <Award className="w-5 h-5" />, text: 'Skill development sessions & workshops' },
    { icon: <Users className="w-5 h-5" />, text: 'Internal training and knowledge sharing' },
    { icon: <Target className="w-5 h-5" />, text: 'Real ownership of projects from Day 1' },
    { icon: <TrendingUp className="w-5 h-5" />, text: 'Clear progression paths and role growth' }
  ];

  const lifeHighlights = [
    'Festival and cultural celebrations',
    'Team lunches & outdoor meets',
    'Employee recognition programs',
    'A workplace filled with positivity & support'
  ];

  return (
    <div className="life-at-aham">

      <SEO
        title="Life at Aham Housing Finance | Careers & Work Culture"
        description="Discover life at Aham Housing Finance – inclusive culture, growth and rewards for people enabling housing for all. Explore careers today."
        keywords="Life at Aham, Aham Housing Finance careers, Aham workplace culture, work at Aham Housing Finance, Aham jobs, careers at Aham, employee experience Aham, Aham team culture, Aham Housing Finance jobs, join Aham Housing Finance, inclusive workplace Aham, employee engagement Aham, Aham company culture, finance company careers India, housing finance careers, growth at Aham, Aham HR culture, women friendly workplace, professional development Aham, Aham career opportunities"
        canonical="https://ahamhfc.com/life-at-aham-housing-finance-careers"
        ogTitle="Life at Aham Housing Finance | Careers & Work Culture"
        ogDescription="Discover life at Aham Housing Finance – inclusive culture, growth and rewards for people enabling housing for all. Explore careers today."
      />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${resolveImageUrl(banner?.image_url || '/img/Banners/Life-At-Banner-new.png')}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {banner?.title || 'Life at Aham'}
            </h1>
            <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-6 md:mb-8">
              {banner?.subtitle || 'Where people grow, ideas evolve, and impact happens every day.'}
            </p>

            {/* Breadcrumb Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="flex items-center space-x-2"
            >
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Life at Aham</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section 1: Our Culture */}
      <section className="culture-section">
        <div className="section-container">
          <div className="section-header text-center lg:text-left">
            <span className="section-label">{cultureSettings?.section_label || 'Our Foundation'}</span>
            <h2 className="section-title mx-auto lg:mx-0">{cultureSettings?.section_title || 'Who We Are'}</h2>
          </div>

          <div className="culture-grid">
            <div className="culture-text text-center lg:text-left">
              <p className="culture-description">
                {cultureSettings?.description || 'At Aham, we believe work should feel meaningful. We are driven by curiosity, ownership, and a shared purpose to build solutions that matter.'}
              </p>
              <p className="culture-description-secondary">
                {cultureSettings?.description_secondary || "We don't just work together, we learn, experiment, challenge, and build together."}
              </p>

              <h3 className="values-heading">{cultureSettings?.values_title || 'Key Values'}</h3>
              <div className="values-grid">
                {(cultureValues.length > 0 ? cultureValues : values).map((value, index) => (
                  <div key={index} className="value-card">
                    <div className="value-icon">{value.icon || getIcon(value.icon_name)}</div>
                    <p className="value-text">{value.title}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="culture-images">
              {(cultureImages.length > 0 ? cultureImages : [
                { image_url: '/img/ahamMoments/who-we-are.webp', caption: 'Our diverse team united by a shared vision. Building meaningful connections every day.' },
                { image_url: '/img/ahamlife/Team-collaboration.webp', caption: 'Collaboration in action — ideas flowing freely. Where teamwork drives innovation.' }
              ]).map((img, index) => (
                <div key={index} className={`culture-image-wrapper image-${index + 1}`}>
                  <img
                    src={resolveImageUrl(img.image_url)}
                    alt="Life at Aham Housing Finance careers and work culture – happy team at an inclusive affordable housing finance company in India."
                    className="culture-img"
                  />
                  <p className="image-caption">
                    <em dangerouslySetInnerHTML={{ __html: img.caption.replace(/\. /g, '.<br />') }}></em>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Work Environment */}
      <section className="environment-section">
        <div className="section-container">
          <div className="section-header centered">
            <span className="section-label">{thriveSettings?.section_label || 'Where We Thrive'}</span>
            <h2 className="section-title">{thriveSettings?.section_title || 'The Environment We Create'}</h2>
            <p className="section-intro">
              {thriveSettings?.description || "Our workspace is built on collaboration and trust. Whether you're solving real business challenges or brainstorming with teammates, you will always find space to create and contribute."}
            </p>
          </div>

          <div className="environment-content">
            <div className="environment-images">
              {thriveImages.length > 0 ? (
                thriveImages.map((img, index) => (
                  <div key={img.id} className="env-image-large mb-6">
                    <img
                      src={resolveImageUrl(img.image_url)}
                      alt="Life at Aham Housing Finance careers and work culture – happy team at an inclusive affordable housing finance company in India."
                    />
                    <p className="image-caption">
                      <em dangerouslySetInnerHTML={{ __html: img.caption ? img.caption.replace(/\. /g, '.<br />') : '' }}></em>
                    </p>
                  </div>
                ))
              ) : (
                <div className="env-image-large">
                  <img
                    src="/img/ahamMoments/Environment.webp"
                    alt="Life at Aham Housing Finance careers and work culture – happy team at an inclusive affordable housing finance company in India."
                  />
                  <p className="image-caption">
                    <em>A workspace designed for creativity and focus.<br />Where every corner inspires productivity.</em>
                  </p>
                </div>
              )}
            </div>

            <Card className="environment-features">
              <h3 className="features-heading">{thriveSettings?.features_title || 'What Makes Our Environment Unique'}</h3>
              <ul className="features-list">
                {(thriveFeatures.length > 0 ? thriveFeatures : [
                  { feature_text: 'Flat communication, no unnecessary hierarchy' },
                  { feature_text: 'Freedom to explore and try new ideas' },
                  { feature_text: 'Supportive team culture' },
                  { feature_text: 'Transparent and constructive feedback systems' }
                ]).map((feature, index) => (
                  <li key={index} className="feature-item">
                    <CheckCircle2 className="feature-icon" />
                    <span>{feature.feature_text || feature}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 3: Growth & Opportunities */}
      <section className="growth-section">
        <div className="section-container">
          <div className="growth-layout">
            <div className="growth-images">
              {journeyImages.length > 0 ? (
                journeyImages.map((img, index) => (
                  <div key={img.id} className={index === 0 ? "growth-image-wrapper primary" : "growth-image-wrapper secondary"}>
                    <img
                      src={resolveImageUrl(img.image_url)}
                      alt="Life at Aham Housing Finance careers and work culture – happy team at an inclusive affordable housing finance company in India."
                    />
                    <p className="image-caption">
                      <em dangerouslySetInnerHTML={{ __html: img.caption ? img.caption.replace(/\. /g, '.<br />') : '' }}></em>
                    </p>
                  </div>
                ))
              ) : (
                <>
                  <div className="growth-image-wrapper primary">
                    <img
                      src="/img/ahamlife/Growth-us.webp"
                      alt="Life at Aham Housing Finance careers and work culture – happy team at an inclusive affordable housing finance company in India."
                    />
                    <p className="image-caption">
                      <em>Learning never stops at Aham.<br />Skill development sessions that empower our team.</em>
                    </p>
                  </div>
                  <div className="growth-image-wrapper secondary">
                    <img
                      src="/img/ahamlife/Mentorship.webp"
                      alt="Life at Aham Housing Finance careers and work culture – happy team at an inclusive affordable housing finance company in India."
                    />
                    <p className="image-caption">
                      <em>Mentorship that shapes futures.<br />Guidance from experienced leaders.</em>
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="growth-content text-center lg:text-left">
              <div className="section-header text-center lg:text-left">
                <span className="section-label">{journeySettings?.section_label || 'Your Journey'}</span>
                <h2 className="section-title mx-auto lg:mx-0">{journeySettings?.section_title || 'Grow With Us'}</h2>
              </div>
              <p className="growth-intro">
                {journeySettings?.description || "People grow here — in skills, confidence, and mindset. We invest in learning, mentorship, and opportunities that help each individual become the best version of themselves."}
              </p>

              <h3 className="opportunities-heading">{journeySettings?.opportunities_title || 'Growth Opportunities'}</h3>
              <div className="opportunities-grid">
                {(journeyOpportunities.length > 0 ? journeyOpportunities : [
                  { opportunity_text: 'Skill development sessions & workshops', icon_name: 'Award' },
                  { opportunity_text: 'Internal training and knowledge sharing', icon_name: 'Users' },
                  { opportunity_text: 'Real ownership of projects from Day 1', icon_name: 'Target' },
                  { opportunity_text: 'Clear progression paths and role growth', icon_name: 'TrendingUp' }
                ]).map((opportunity, index) => (
                  <div key={index} className="opportunity-card">
                    <div className="opportunity-icon">{getIcon(opportunity.icon_name)}</div>
                    <p className="opportunity-text">{opportunity.opportunity_text || opportunity.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Life Beyond Work */}
      <section className="life-section">
        <div className="section-container">
          <div className="section-header centered">
            <span className="section-label">{beyondSettings?.section_label || 'Beyond Work'}</span>
            <h2 className="section-title">{beyondSettings?.section_title || 'Life Beyond Work'}</h2>
            <p className="section-intro">
              {beyondSettings?.description || "At Aham, work is important, but people come first. We celebrate together, learn together, and make memories along the way. From team outings to internal events and celebrations, we enjoy the journey as much as the work."}
            </p>
          </div>

          <div className="life-content">
            <div className="life-images">
              {(beyondImages.length > 0 ? beyondImages : [
                { image_url: '/img/ahamlife/Team-celebration.webp', caption: 'Celebrations that bring us closer. Cherishing milestones together as one family.' },
                { image_url: '/img/ahamlife/Workplace-fun.webp', caption: 'Fun moments that spark joy at work. Laughter and camaraderie in every corner.' }
              ]).map((img, index) => (
                <div key={index} className="life-image-card">
                  <img
                    src={resolveImageUrl(img.image_url)}
                    alt="Life at Aham Housing Finance careers and work culture – happy team at an inclusive affordable housing finance company in India."
                  />
                  <p className="image-caption">
                    <em dangerouslySetInnerHTML={{ __html: img.caption ? img.caption.replace(/\. /g, '.<br />') : '' }}></em>
                  </p>
                </div>
              ))}
            </div>

            <div className="highlights-grid">
              {(beyondHighlights.length > 0 ? beyondHighlights : [
                'Festival and cultural celebrations',
                'Team lunches & outdoor meets',
                'Employee recognition programs',
                'A workplace filled with positivity & support'
              ]).map((highlight, index) => (
                <Card key={index} className="highlight-card">
                  <CheckCircle2 className="highlight-icon" />
                  <p className="highlight-text">{highlight.highlight_text || highlight}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Awards & Recognitions */}
      <section className="awards-section">
        <div className="section-container">
          <div className="awards-layout">
            {/* Left Side - Content */}
            <div className="awards-content text-center lg:text-left">
              <div className="section-header text-center lg:text-left">
                <span className="section-label">{recognitionSettings?.section_label || 'Recognition & Excellence'}</span>
                <h2 className="section-title mx-auto lg:mx-0">{recognitionSettings?.section_title || 'Our Awards & Achievements'}</h2>
              </div>

              <p className="awards-intro">
                {recognitionSettings?.description || "At Aham Housing Finance, excellence isn't just a goal—it's our standard. Our commitment to providing innovative financial solutions and exceptional customer service has been recognized by industry leaders and prestigious institutions across the nation."}
              </p>

              <div className="awards-list">
                {(recognitionAwards.length > 0 ? recognitionAwards : [
                  { award_title: 'Best Housing Finance Company 2024', award_description: 'Recognized for outstanding contribution to affordable housing and customer-centric lending solutions', award_year: '2024' },
                  { award_title: 'Excellence in Financial Innovation', award_description: 'Honored for pioneering interview-based credit assessment and serving customers without formal income proof', award_year: '2023' },
                  { award_title: 'Top Employer in South India', award_description: 'Recognized for creating an inclusive workplace culture and exceptional employee development programs', award_year: '2023' },
                  { award_title: 'Customer Service Excellence Award', award_description: 'Awarded for maintaining highest customer satisfaction scores and transparent lending practices', award_year: '2022' }
                ]).map((award, index) => (
                  <div key={index} className="award-item">
                    <div className="award-icon-wrapper">
                      <Award className="award-icon" />
                    </div>
                    <div className="award-details">
                      <h4 className="award-title">{award.award_title}</h4>
                      <p className="award-description">{award.award_description}</p>
                      <span className="award-year">{award.award_year}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* <div className="awards-stats">
                <div className="stat-card">
                  <div className="stat-number">15+</div>
                  <div className="stat-label">Industry Awards</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">98%</div>
                  <div className="stat-label">Customer Satisfaction</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">4.8/5</div>
                  <div className="stat-label">Industry Rating</div>
                </div>
              </div> */}
            </div>

            {/* Right Side - Images */}
            <div className="awards-images">
              {(recognitionImages.length > 0 ? recognitionImages : [
                { image_url: '/img/ahamlife/Team-celebration.webp', caption: 'Celebrating excellence together. Recognition for our collective dedication.', overlay_text: 'Award Winning Team' },
                { image_url: '/img/ahamlife/Team-banner.webp', caption: 'Proud moments of achievement. Our team celebrating industry recognition.', overlay_text: 'Industry Leaders' }
              ]).map((img, index) => (
                <div key={index} className={`awards-image-wrapper ${index === 0 ? 'primary-award' : 'secondary-award'}`}>
                  <img
                    src={resolveImageUrl(img.image_url)}
                    alt="Life at Aham Housing Finance careers and work culture – happy team at an inclusive affordable housing finance company in India."
                    className="awards-img"
                  />
                  {img.overlay_text && (
                    <div className="image-overlay">
                      <div className="overlay-badge">
                        <Award className="overlay-icon" />
                        <span>{img.overlay_text}</span>
                      </div>
                    </div>
                  )}
                  <p className="image-caption">
                    <em dangerouslySetInnerHTML={{ __html: img.caption ? img.caption.replace(/\. /g, '.<br />') : '' }}></em>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">{ctaSettings?.title || 'Ready to Join Us?'}</h2>
          <p className="cta-text">
            {ctaSettings?.description || 'Join us and be part of a workplace where your work has meaning, your voice has value, and your growth has no boundaries.'}
          </p>
          <div className="cta-buttons">
            {ctaSettings?.button_text && (
              <Button
                variant="outline"
                className="cta-secondary"
                onClick={() => setIsModalOpen(true)}
              >
                {ctaSettings.button_text}
              </Button>
            )}
          </div>
        </div>
      </section >

      <LifeAtAhamModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

    </div >
  );
};

export default LifeAtAham;